import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Button, Form, Row, Col, Spinner, Badge, Nav, InputGroup } from 'react-bootstrap';
import { 
    FaPlus, FaMinus, FaTools, FaCogs, FaBolt, FaCheckCircle, 
    FaExclamationTriangle, FaGift, FaCopy, FaExternalLinkAlt, FaRocket, FaShieldAlt
} from 'react-icons/fa';
import api from '../services/api'; 
import moment from 'moment';

function Pricing({ setCurrentView, setOrderToPay }) {
    const [systemConfig, setSystemConfig] = useState({}); 
    const [availableBots, setAvailableBots] = useState([]);
    const [selectedBot, setSelectedBot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [orderType, setOrderType] = useState('official'); 
    
    // --- [MỤC C]: THÊM STATE QUẢN LÝ SỐ SAO AN TOÀN ---
    const [safetyStars, setSafetyStars] = useState(3); // Mặc định ban đầu là 3 Sao
    
    const [form, setForm] = useState({ 
        accountId: localStorage.getItem('last_mt5_id') || '', 
        capital: 700, 
        setupType: 'probot_install' 
    });

    const [isAgreed, setIsAgreed] = useState(false);
    const [isExnessReferralConfirmed, setIsExnessReferralConfirmed] = useState(false);
    const [isUSCAccountConfirmed, setIsUSCAccountConfirmed] = useState(false); 

    const IB_CODE = "bfoz1gnn4z";
    const EXNESS_LINK = `https://one.exnessonelink.com/a/${IB_CODE}?source=app`;

    const handleAccountIdChange = (e) => {
        const val = e.target.value;
        setForm(prev => ({ ...prev, accountId: val }));
        localStorage.setItem('last_mt5_id', val);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(IB_CODE);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const botCategory = useMemo(() => {
        if (!selectedBot) return null;
        const pCode = (selectedBot.package_code || "").toUpperCase();
        return (pCode === "BOT USD") ? 'BOT_2' : 'BOT_1';
    }, [selectedBot]);

    const isBotType1 = botCategory === 'BOT_1';
    const isCapitalValid = useMemo(() => form.capital >= 300, [form.capital]);
    const showUSCCheckbox = useMemo(() => isBotType1 && Number(form.capital) < 30000, [isBotType1, form.capital]);

    const totals = useMemo(() => {
        return (form.setupType === 'self_install') ? 0 : (Number(systemConfig.setup_fee_vnd) || 30000);
    }, [form.setupType, systemConfig]);

    const getNextSaturdayDeadline = () => {
        const now = new Date();
        const resultDate = new Date();
        const currentDay = now.getDay();
        const daysUntilThisSaturday = 6 - currentDay;
        resultDate.setDate(now.getDate() + daysUntilThisSaturday + 7);
        resultDate.setHours(23, 59, 59, 999);
        return resultDate;
    };

    const handleOrder = async () => {
        if (!form.accountId) return alert("Vui lòng nhập ID MT5");
        setIsSubmitting(true);

        try {
            const capitalUSD = Number(form.capital);
            const pCode = (selectedBot?.package_code || "").toUpperCase();
            const currentBotType = (pCode === "BOT USD") ? 'BOT_2' : 'BOT_1';
            
            let finalCurrency = (currentBotType === 'BOT_1' && capitalUSD < 30000) ? 'USC' : 'USD';
            const divisor = (finalCurrency === 'USC') ? 10 : 300;
            
            let finalLotSize = Number(((capitalUSD / divisor) * 0.01).toFixed(2));
            if (finalLotSize < 0.01) finalLotSize = 0.01;

            const expirationDate = getNextSaturdayDeadline();
            const hwidLockValue = orderType === 'trial' ? 'TRIAL' : 'REAL';

            const payload = { 
                packageId: selectedBot?._id, 
                accountId: String(form.accountId).trim(), 
                capital: capitalUSD, 
                lotsize: finalLotSize, 
                currency: finalCurrency, 
                setupType: String(form.setupType), 
                totalPrice: Number(totals), 
                billingType: orderType === 'trial' ? 'free_trial' : 'profit_sharing', 
                contractType: orderType, 
                magic: Number(selectedBot?.magic_number) || 0, 
                isCustomLot: true,
                expiredAt: expirationDate,
                hwid_lock: hwidLockValue,
                safety_stars: Number(safetyStars) 
            };

            console.log("--- [DEBUG] PAYLOAD SENT (WITH STARS):", payload);

            // Gọi API chốt đơn an toàn mang theo token tươi của Interceptor
            const res = await api.post('/api/order/place-order', payload);

            if (res.data.success) {
                if (typeof setOrderToPay === 'function') {
                    setOrderToPay(res.data.order);
                }
                if (typeof setCurrentView === 'function') {
                    setCurrentView('transaction-history'); 
                }
                alert(`Tạo đơn thành công! Cấp độ an toàn: ${safetyStars} Sao`);
            }
        } catch (err) { 
            console.error("Lỗi đặt đơn:", err);
            alert(err.response?.data?.message || "Có lỗi xảy ra trong quá trình đặt đơn!");
        } finally { 
            setIsSubmitting(false); 
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const resData = await api.get(`/api/public/pricing-data`);
                const bots = resData.data.botSetups || [];
                setAvailableBots(bots);
                setSystemConfig(resData.data.config || {});

                if (bots.length > 0) {
                    const pendingBotRaw = localStorage.getItem('pending_bot_selection');
                    if (pendingBotRaw) {
                        const pendingBot = JSON.parse(pendingBotRaw);
                        const foundBot = bots.find(b => b._id === pendingBot._id);
                        if (foundBot) {
                            setSelectedBot(foundBot);
                        } else {
                            setSelectedBot(bots[0]);
                        }
                    } else {
                        setSelectedBot(bots[0]);
                    }
                }
                setLoading(false);
            } catch (err) { 
                console.error("Lỗi khởi tạo dữ liệu:", err);
                setLoading(false); 
            }
        };
        fetchInitialData();
    }, []);

    if (loading) return <div className="loader-container"><Spinner animation="border" variant="warning" /></div>;

    return (
        <Container className="pricing-premium-layout py-5">
            <div className="text-center mb-5">
                <Badge bg="none" className="border border-warning text-warning mb-2 px-3 py-2 uppercase tracking-widest font-mono shadow-gold-active">
                    NeXBot Intelligence v4.0
                </Badge>
                <h1 className="fw-bold text-white header-title font-main">Bảng Điều Khiển Kích Hoạt</h1>
                <p className="text-white-50 font-main">Thiết lập tham số và vận hành hệ thống giao dịch tự động</p>
                
                <div className="d-flex justify-content-center mt-4">
                    <Nav className="pill-switcher p-1 shadow-lg">
                        <Nav.Link active={orderType === 'official'} onClick={() => setOrderType('official')} className="font-mono">CHÍNH THỨC</Nav.Link>
                        <Nav.Link active={orderType === 'trial'} onClick={() => setOrderType('trial')} className="font-mono"><FaGift className="me-1"/> DÙNG THỬ</Nav.Link>
                    </Nav>
                </div>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="glass-card mb-4 border-0 shadow-gold-active">
                        <Card.Body className="p-4 p-md-5">
                            {/* KHỐI 1: QUY MÔ DÒNG VỐN */}
                            <div className="setup-section mb-5">
                                <h5 className="section-heading font-mono"><FaRocket className="me-2 text-warning"/> Quy mô dòng vốn</h5>
                                <div className="capital-modern-rect-box shadow-sm">
                                    <button 
                                        type="button"
                                        className="cap-rect-btn"
                                        onClick={() => setForm(f => ({...f, capital: Math.max(0, f.capital - 100)}))}
                                    >
                                        <FaMinus size={12}/>
                                    </button>

                                    <div className="cap-rect-input-wrapper">
                                        <input 
                                            type="number" 
                                            className="cap-rect-input-field font-mono"
                                            value={form.capital} 
                                            onChange={(e) => setForm({...form, capital: Number(e.target.value)})}
                                        />
                                        <span className="cap-rect-unit font-mono">USD</span>
                                    </div>

                                    <button 
                                        type="button"
                                        className="cap-rect-btn"
                                        onClick={() => setForm(f => ({...f, capital: f.capital + 100}))}
                                    >
                                        <FaPlus size={12}/>
                                    </button>
                                </div>
                                {!isCapitalValid && <div className="text-danger text-center mt-2 small fw-bold anim-blink font-mono"><FaExclamationTriangle/> Vốn tối thiểu yêu cầu: 300$</div>}
                            </div>

                            {/* KHỐI 2: CHỌN CHIẾN THUẬT & IB */}
                            <Row className="mb-4 g-4">
                                <Col md={6}>
                                    <h5 className="section-heading small-cap mb-3 font-mono">Hệ thống thuật toán</h5>
                                    <Form.Select 
                                        className="premium-select font-mono" 
                                        value={selectedBot?._id} 
                                        onChange={(e) => setSelectedBot(availableBots.find(b => b._id === e.target.value))}
                                    >
                                        {[...availableBots]
                                            .sort((a, b) => (a._id === selectedBot?._id ? -1 : b._id === selectedBot?._id ? 1 : 0))
                                            .map(bot => (
                                                <option key={bot._id} value={bot._id}>
                                                    {bot.name?.toUpperCase()} {bot._id === JSON.parse(localStorage.getItem('pending_bot_selection'))?._id ? " (BẠN ĐÃ CHỌN)" : ""}
                                                </option>
                                            ))
                                        }
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <h5 className="section-heading small-cap mb-3 font-mono">Link giới thiệu Exness(IB)</h5>
                                    <InputGroup className="premium-input-group">
                                        <Form.Control className="ib-field font-mono" value={IB_CODE} readOnly />
                                        <Button className="btn-copy-gold-bright font-mono" onClick={copyToClipboard}>{copySuccess ? 'XONG' : <FaCopy/>}</Button>
                                        <Button variant="outline-light" className="btn-link-icon" href={EXNESS_LINK} target="_blank"><FaExternalLinkAlt/></Button>
                                    </InputGroup>
                                </Col>
                            </Row>

                            {/* --- CHỌN SAO BẢO MẬT (ĐỒNG BỘ FIRA CODE & SÁNG ĐÈN CHÂN THỰC) --- */}
                            <div className="setup-section mb-5">
                                <h5 className="section-heading font-mono"><FaShieldAlt className="me-2 text-warning"/> Cấp độ bảo vệ vốn & Chia Lot</h5>
                                <Row className="g-3">
                                    {[3, 4, 5].map((star) => (
                                        <Col key={star} xs={4}>
                                            <div 
                                                className={`option-card star-card glow-gold-box ${safetyStars === star ? 'selected' : ''}`} 
                                                onClick={() => setSafetyStars(star)}
                                                style={{ padding: '15px', textAlign: 'center' }}
                                            >
                                                <div className="star-title font-mono" style={{ color: safetyStars === star ? '#fbbf24' : '#fff', fontSize: '1.1rem', fontWeight: '700' }}>
                                                    {star} SAO
                                                </div>
                                                <div className="star-desc font-mono text-white-50 mt-1" style={{ fontSize: '0.7rem' }}>
                                                    {star === 3 ? 'Lot Tiêu Chuẩn' : star === 4 ? '1/2 Lot tiêu chuẩn' : '1/3 Lot tiêu chuẩn'}
                                                </div>
                                                <div className="check-mark"><FaCheckCircle/></div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </div>

                            {/* KHỐI 3: PHƯƠNG THỨC VẬN HÀNH */}
                            <h5 className="section-heading small-cap mb-3 font-mono">Phương thức vận hành</h5>
                            <Row className="g-3 mb-3">
                                <Col sm={6}>
                                    <div className={`option-card ${form.setupType === 'probot_install' ? 'selected' : ''}`} onClick={() => setForm({...form, setupType: 'probot_install'})}>
                                        <div className="d-flex align-items-center">
                                            <div className="icon-wrap"><FaCogs/></div>
                                            <div>
                                                <div className="opt-title font-mono">Chạy Bot trên VPS</div>
                                                <div className="opt-price font-mono">{(Number(systemConfig.setup_fee_vnd) || 30000).toLocaleString()}đ / tháng</div>
                                            </div>
                                        </div>
                                        <div className="check-mark"><FaCheckCircle/></div>
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <div className={`option-card cyan ${form.setupType === 'self_install' ? 'selected' : ''}`} onClick={() => setForm({...form, setupType: 'self_install'})}>
                                        <div className="d-flex align-items-center">
                                            <div className="icon-wrap"><FaTools/></div>
                                            <div>
                                                <div className="opt-title font-mono">Chạy Bot trên Máy</div>
                                                <div className="opt-price font-mono">Miễn phí</div>
                                            </div>
                                        </div>
                                        <div className="check-mark"><FaCheckCircle/></div>
                                    </div>
                                </Col>
                            </Row>

                            {form.setupType === 'probot_install' ? (
                                <div className="font-mono" style={{ background: 'rgba(251, 191, 36, 0.04)', border: '1px solid rgba(251, 191, 36, 0.2)', padding: '15px', borderRadius: '12px', fontSize: '0.8rem', color: '#fbbf24' }}>
                                    🚀 <strong>Cấu hình VPS Cao cấp:</strong> Hệ thống Cloud của NeXBot sẽ tự động thiết lập và chạy đồng thời <strong>BỘ 3 BOT THUẬT TOÁN ĐA TẦNG</strong> trên tài khoản của bạn. Vốn phân bổ sẽ được chia đều để tối đa hóa lợi nhuận.
                                </div>
                            ) : (
                                <div className="font-mono" style={{ background: 'rgba(0, 242, 255, 0.04)', border: '1px solid rgba(0, 242, 255, 0.2)', padding: '15px', borderRadius: '12px', fontSize: '0.8rem', color: '#00f2ff' }}>
                                    💻 <strong>Lưu ý tự cài đặt:</strong> Bạn cần tải xuống máy tính ứng dụng MT5 sàn Exness hoặc XM và duy trì mở máy liên tục 24/5 cho cả <strong>3 Bot</strong> để đảm bảo đồng bộ tín hiệu trọn vẹn.
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <div className="sticky-top" style={{top: '20px'}}>
                        <Card className="glass-card bill-card border-0 overflow-hidden bill-glow-active">
                            <div className="bill-header p-4 text-center">
                                <h6 className="m-0 tracking-widest font-mono text-warning">XÁC NHẬN TÀI KHOẢN</h6>
                            </div>
                            <Card.Body className="p-4">
                                <div className="mb-4">
                                    <label className="label-modern font-mono">Số tài khoản giao dịch</label>
                                    <input 
                                        type="number" 
                                        className="mt5-input font-mono" 
                                        placeholder="Nhập số TK giao dịch" 
                                        value={form.accountId} 
                                        onChange={handleAccountIdChange}
                                    />
                                    <div className="text-center mt-1"><small className="text-info font-mono" style={{fontSize: '0.7rem'}}>Hệ thống đã ghi nhớ ID này</small></div>
                                </div>

                                <div className="checklist-modern mb-4 font-mono">
                                    <div className="check-row">
                                        <Form.Check type="checkbox" checked={isExnessReferralConfirmed} onChange={(e) => setIsExnessReferralConfirmed(e.target.checked)} label="Xác nhận mã IB đối tác"/>
                                    </div>
                                    {showUSCCheckbox && (
                                        <div className="check-row highlight">
                                            <Form.Check type="checkbox" checked={isUSCAccountConfirmed} onChange={(e) => setIsUSCAccountConfirmed(e.target.checked)} label="Tôi dùng tài khoản CENT"/>
                                        </div>
                                    )}
                                    <div className="check-row">
                                        <Form.Check type="checkbox" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} label="Đồng ý chia sẻ lợi nhuận"/>
                                    </div>
                                </div>

                                <div className="bill-summary p-3 rounded-3 mb-4 font-mono">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-white-50 small">Cấu hình bảo vệ:</span>
                                        <span className="text-warning small fw-bold">{safetyStars} SAO</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-white-50 small">Loại tài khoản:</span>
                                        <span className="text-white small fw-bold">{orderType === 'trial' ? 'Dùng thử 7 ngày' : 'Chính thức'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-white fw-bold small">THANH TOÁN:</span>
                                        <span className="text-warning h4 fw-bold mb-0">{(totals).toLocaleString()}đ</span>
                                    </div>
                                </div>

                                <Button 
                                    className={`btn-grand-action w-100 font-mono ${orderType === 'trial' ? 'trial' : 'pro'}`}
                                    disabled={isSubmitting || !form.accountId || !isAgreed || !isExnessReferralConfirmed || (showUSCCheckbox && !isUSCAccountConfirmed) || !isCapitalValid}
                                    onClick={handleOrder}
                                >
                                    {isSubmitting ? <Spinner size="sm" animation="border" /> : (orderType === 'trial' ? 'BẮT ĐẦU NGAY' : 'KÍCH HOẠT HỆ THỐNG')}
                                </Button>
                                
                                <div className="text-center mt-3">
                                    <small className="text-white-50 font-mono" style={{fontSize: '0.65rem'}}><FaShieldAlt className="me-1"/> Đã mã hóa bảo mật đầu cuối</small>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </Col>
            </Row>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;700&display=swap');

                .font-main { font-family: 'Inter', sans-serif !important; }
                .font-mono { font-family: 'Fira Code', monospace !important; }
                
                .pricing-premium-layout { background: #020617; min-height: 100vh; color: #fff; }
                .header-title { font-size: 2.5rem; background: linear-gradient(to right, #fff, #fbbf24); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                
                .glass-card { background: rgba(15, 23, 42, 0.8); backdrop-filter: blur(10px); border-radius: 20px; border: 1px solid rgba(255,255,255,0.05) !important; }
                
                /* SÁNG ĐÈN CHO CARD TỔNG QUAN HÓA ĐƠN */
                .bill-card.bill-glow-active {
                    background: linear-gradient(145deg, #0f172a 0%, #020617 100%);
                    border: 1px solid #fbbf24 !important;
                    border-radius: 20px;
                    box-shadow: 0 0 35px rgba(251, 191, 36, 0.25), inset 0 0 20px rgba(251, 191, 36, 0.08) !important;
                }

                .pill-switcher { background: #0f172a; border-radius: 50px; border: 1px solid rgba(255,255,255,0.08); }
                .pill-switcher .nav-link { color: #64748b; border-radius: 50px; padding: 10px 25px; font-weight: 700; font-size: 0.75rem; }
                .pill-switcher .nav-link.active { background: #fbbf24; color: #000; box-shadow: 0 0 15px rgba(251, 191, 36, 0.3); }
                
                .capital-modern-rect-box { background: #0f172a; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .cap-rect-btn { width: 34px; height: 36px; border-radius: 8px; border: none; background: #1e293b; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; }
                .cap-rect-btn:hover { background: #fbbf24; color: #000; }
                
                .cap-rect-input-field { background: transparent; border: none; color: #fbbf24; font-size: 1.5rem; font-weight: 700; width: 110px; text-align: center; outline: none; }
                .cap-rect-unit { color: #475569; font-weight: 700; font-size: 0.85rem; }
                
                .section-heading { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: 700; margin-bottom: 15px; }
                
                .premium-select { background: #0f172a !important; border: 1px solid rgba(255,255,255,0.08) !important; color: #fff !important; padding: 12px !important; border-radius: 10px !important; }
                .premium-input-group { background: #0f172a; border: 1px solid rgba(255,255,255,0.08); border-radius: 10px; overflow: hidden; }
                .ib-field { background: transparent !important; border: none !important; color: #22d3ee !important; font-weight: 700 !important; }
                
                /* NÚT COPY MÀU VÀNG SÁNG RÕ HƠN */
                .btn-copy-gold-bright { 
                    background: #fbbf24 !important; 
                    color: #000 !important; 
                    border: none !important; 
                    font-weight: 700 !important; 
                    font-size: 0.75rem !important;
                    padding: 0 20px !important;
                    transition: 0.2s ease-in-out;
                }
                .btn-copy-gold-bright:hover {
                    background: #f59e0b !important;
                    box-shadow: 0 0 12px #fbbf24;
                }

                .btn-link-icon { border: none !important; color: #64748b !important; background: none !important; }
                .btn-link-icon:hover { color: #fff !important; }

                /* HIỆU ỨNG SÁNG CARD CHỌN SAO BẢO MẬT */
                .option-card { background: #0f172a; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 15px; cursor: pointer; position: relative; transition: 0.3s; }
                .option-card.selected { 
                    border-color: #fbbf24 !important; 
                    background: rgba(251, 191, 36, 0.04) !important; 
                    box-shadow: 0 0 20px rgba(251, 191, 36, 0.15);
                }
                .option-card.cyan.selected { border-color: #00f2ff !important; background: rgba(0, 242, 255, 0.04) !important; }
                
                .opt-title { font-weight: 700; color: #fff; font-size: 0.9rem; }
                .opt-price { color: #64748b; font-size: 0.8rem; }
                .icon-wrap { width: 40px; height: 45px; display: flex; align-items: center; font-size: 1.1rem; color: #475569; }
                .option-card.selected .icon-wrap { color: #fbbf24; }

                .check-mark { position: absolute; top: 8px; right: 10px; color: #fbbf24; opacity: 0; transition: 0.2s; font-size: 0.8rem; }
                .option-card.selected .check-mark { opacity: 1; }
                
                .mt5-input { background: #020617; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; width: 100%; padding: 12px; color: #fff; font-size: 1.3rem; font-weight: 700; text-align: center; outline: none; transition: 0.2s; }
                .mt5-input:focus { border-color: #fbbf24; box-shadow: 0 0 10px rgba(251, 191, 36, 0.2); }
                
                .check-row { background: rgba(255,255,255,0.01); padding: 10px; border-radius: 8px; margin-bottom: 6px; font-size: 0.8rem; color: #64748b; }
                .check-row.highlight { color: #00f2ff; }
                .bill-summary { background: #020617; border: 1px solid rgba(255,255,255,0.04); }
                
                .btn-grand-action { padding: 14px; border-radius: 12px; border: none; font-weight: 700; font-size: 0.95rem; transition: 0.2s; }
                .btn-grand-action.pro { background: #fbbf24; color: #000; }
                .btn-grand-action.pro:hover { background: #f59e0b; box-shadow: 0 0 15px rgba(251, 191, 36, 0.3); }
                .btn-grand-action.trial { background: #00f2ff; color: #000; }
                .btn-grand-action:disabled { opacity: 0.15; }
                
                .label-modern { font-size: 9px; text-transform: uppercase; color: #475569; letter-spacing: 2px; margin-bottom: 6px; display: block; }
                .anim-blink { animation: blink 1s infinite; }
                @keyframes blink { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
                input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            `}</style>
        </Container>
    );
}

export default Pricing;